import { useCallback, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useParams } from 'react-router-dom';
import { DeleteOutlined, EditOutlined, FileTextOutlined, FileWordOutlined } from '@ant-design/icons';
import { AudioLines } from 'lucide-react';

import { PageHeader } from '../../../components/shared';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Empty, Skeleton } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { ngayGioVN } from '../../../lib/dinh-dang';
import { useToast } from '../../../lib/toast';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import { BanChepLoi, CanhBaoDoTinCay, KhungBanChepLoi } from '../components/BanChepLoi';
import { HopDoiTenPhien } from '../components/HopDoiTenPhien';
import { NguoiNoiPhien } from '../components/NguoiNoiPhien';
import { TienTrinhXuLy } from '../components/TienTrinhXuLy';
import { TomTatPhien } from '../components/TomTatPhien';
import { TrinhPhatAm } from '../components/TrinhPhatAm';
import type { DieuKhienPhat } from '../components/TrinhPhatAm';
import { NHAN_TRANG_THAI, QUYEN } from '../constants';
import { useChiTietPhien, useDoanPhien, useGhiPhien } from '../hooks/useBienBan';
import { useXuatPhien } from '../hooks/useXuatPhien';
import { coBanChepLoi, dinhDangThoiLuong, timDoanDangPhat } from '../nghiep-vu/bien-ban';

const LA_O_NHAP = 'input, textarea, select, [contenteditable="true"], [role="slider"], [role="combobox"]';

/**
 * Chi tiết phiên (`/bien-ban/:id`): trái là trình phát và bản chép lời, phải là người nói và tóm tắt. Dưới `lg` xếp dọc.
 *
 * Mốc phát chỉ thành state khi **đổi đoạn**, không phải mỗi nhịp `timeupdate`: bản chép lời dài vẽ lại bốn lần một giây là giật.
 */
export function ChiTietPhienView() {
  const { id } = useParams<{ id: string }>();
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const dieuHuong = useDieuHuong();
  const quyen = useModulePermissions(QUYEN);
  const q = useChiTietPhien(id);
  const p = q.data;
  const qDoan = useDoanPhien(id, p?.status);
  const ghi = useGhiPhien();
  const { xuat, dangXuat } = useXuatPhien();
  const [xoa, setXoa] = useState(false);
  const [doiTen, setDoiTen] = useState(false);
  const [idDangPhat, setIdDangPhat] = useState<string | null>(null);
  const phat = useRef<DieuKhienPhat>(null);

  const ds = useMemo(() => [...(qDoan.data ?? [])].sort((a, b) => a.start - b.start), [qDoan.data]);
  const dsRef = useRef(ds);
  dsRef.current = ds;

  const khiThoiGian = useCallback((giay: number) => setIdDangPhat(timDoanDangPhat(dsRef.current, giay)), []);
  const onTua = useCallback((giay: number) => phat.current?.tua(giay), []);

  const phimTat = (e: KeyboardEvent) => {
    if ((e.target as HTMLElement).closest(LA_O_NHAP) || e.ctrlKey || e.metaKey || e.altKey) return;
    const k = e.key.toLowerCase();
    if (k === 'k') phat.current?.batTat();
    else if (k === 'j') phat.current?.tuaTuongDoi(-5);
    else if (k === 'l') phat.current?.tuaTuongDoi(5);
  };

  const bocTrang = (noiDung: React.ReactNode) => <div className="flex h-full flex-col bg-subtle"><div className="flex-1 space-y-4 overflow-y-auto p-4">{noiDung}</div></div>;
  const lienKetVe = { label: t('bienBan.chiTiet.veDanhSach'), onClick: () => dieuHuong('/bien-ban') };

  if (q.isLoading) {
    return bocTrang(
      <div aria-busy className="space-y-4">
        <Card className="space-y-3 px-6 py-4">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-6 w-80 max-w-full" />
        </Card>
        <div className="grid items-start gap-4 lg:grid-cols-5">
          <Card className="space-y-4 p-5 lg:col-span-3">
            <Skeleton className="h-14 w-full" />
            {['w-11/12', 'w-3/4', 'w-5/6', 'w-2/3', 'w-4/5'].map((r) => <Skeleton key={r} className={`h-4 ${r}`} />)}
          </Card>
          <Card className="space-y-4 p-5 lg:col-span-2">
            <Skeleton className="h-5 w-32" />
            {['w-full', 'w-5/6', 'w-2/3', 'w-4/5'].map((r) => <Skeleton key={r} className={`h-4 ${r}`} />)}
          </Card>
        </div>
      </div>,
    );
  }
  if (q.error && !p) {
    const khongThay = q.error.status === 404;
    return bocTrang(
      <>
        <PageHeader breadcrumb={[lienKetVe]} title={t('bienBan.tieuDe')} />
        {khongThay ? (
          <Card><Empty icon={AudioLines} title={t('bienBan.chiTiet.khongThay')} description={t('bienBan.chiTiet.khongThayMo')} /></Card>
        ) : (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-default bg-surface px-4 py-3">
            <span className="min-w-0 text-sm text-danger-700">{t('bienBan.chiTiet.loiTai', { loi: dichLoi(q.error) })}</span>
            <Button variant="outline" size="sm" onClick={() => void q.refetch()}>{t('chung.thuLai')}</Button>
          </div>
        )}
      </>,
    );
  }
  if (!p || !id) return null;

  const coLoi = coBanChepLoi(p.status);
  const rong = !coLoi ? t('bienBan.loi.rongXuLy') : ds.length === 0 ? t('bienBan.loi.rong') : null;

  return bocTrang(
    <>
      <PageHeader
        breadcrumb={[lienKetVe, { label: p.title }]}
        titleIcon={<AudioLines size={20} className="text-primary-600" aria-hidden />}
        title={
          <span className="flex min-w-0 items-center gap-1">
            <span className="min-w-0 truncate" title={p.title}>{p.title}</span>
            {quyen.sua && (
              <Button variant="ghost" size="sm" className="shrink-0" aria-label={t('bienBan.doiTen')} title={t('bienBan.doiTen')} icon={<EditOutlined />} onClick={() => setDoiTen(true)} />
            )}
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge variant={NHAN_TRANG_THAI[p.status].variant}>{t(NHAN_TRANG_THAI[p.status].khoa)}</Badge>
            <span>
              {dinhDangThoiLuong(p.duration_seconds)
                ? t('bienBan.chiTiet.thongTin', { thoiLuong: dinhDangThoiLuong(p.duration_seconds) ?? '', ngay: ngayGioVN(p.created_at, locale) })
                : ngayGioVN(p.created_at, locale)}
            </span>
          </span>
        }
        actions={
          <>
            {p.status === 'DONE' && (
              <>
                <Button variant="outline" size="md" icon={<FileWordOutlined />} loading={dangXuat === 'docx'} onClick={() => xuat(p, 'docx')}>{t('bienBan.xuatWord')}</Button>
                <Button variant="outline" size="md" icon={<FileTextOutlined />} loading={dangXuat === 'txt'} onClick={() => xuat(p, 'txt')}>{t('bienBan.xuatVanBan')}</Button>
              </>
            )}
            {quyen.xoa && (
              <Button variant="outline" danger size="md" icon={<DeleteOutlined />} onClick={() => setXoa(true)}>{t('bienBan.xoaPhien')}</Button>
            )}
          </>
        }
      />

      <TienTrinhXuLy
        phien={p}
        coSua={quyen.sua}
        dangThuLai={ghi.thuLai.isPending}
        onThuLai={() => ghi.thuLai.mutate(p.id, {
          onSuccess: () => toast.success(t('bienBan.daThuLai')),
          onError: (e) => toast.error(dichLoi(e)),
        })}
      />

      <div className="grid items-start gap-4 lg:grid-cols-5">
        <div className="min-w-0 lg:col-span-3" onKeyDown={phimTat}>
          <Card>
            <CardHeader><CardTitle>{t('bienBan.loi.tieuDe')}</CardTitle></CardHeader>
            <CardBody className="space-y-3 py-4">
              <TrinhPhatAm ref={phat} phienId={p.id} thoiLuong={p.duration_seconds} onThoiGian={khiThoiGian} />
              {ds.length > 0 && <CanhBaoDoTinCay muc={p.reliability} lyDo={p.reliability_note} />}
              <KhungBanChepLoi
                dangTai={coLoi && qDoan.isLoading}
                loi={qDoan.error ? t('bienBan.loi.loiTai', { loi: dichLoi(qDoan.error) }) : null}
                onThuLai={() => void qDoan.refetch()}
                rong={rong}
              >
                <BanChepLoi phienId={p.id} ds={ds} speakers={p.speakers} idDangPhat={idDangPhat} coSua={quyen.sua} onTua={onTua} />
              </KhungBanChepLoi>
            </CardBody>
          </Card>
        </div>
        <div className="min-w-0 space-y-4 lg:col-span-2">
          <NguoiNoiPhien phienId={p.id} speakers={p.speakers} coSua={quyen.sua} />
          <TomTatPhien phien={p} coSua={quyen.sua} />
        </div>
      </div>

      {doiTen && <HopDoiTenPhien phien={p} onClose={() => setDoiTen(false)} />}
      <HopXacNhanThaoTac
        open={xoa}
        tieuDe={t('bienBan.xacNhanXoa.tieuDe', { ten: p.title })}
        than={t('bienBan.xacNhanXoa.than')}
        nhanXacNhan={t('bienBan.xacNhanXoa.nut')}
        nguyHiem
        dangChay={ghi.xoa.isPending}
        loi={ghi.xoa.error ? dichLoi(ghi.xoa.error) : null}
        onClose={() => {
          setXoa(false);
          ghi.xoa.reset();
        }}
        onXacNhan={() => ghi.xoa.mutate(p.id, {
          onSuccess: () => {
            toast.success(t('bienBan.daXoa', { ten: p.title }));
            dieuHuong('/bien-ban', { replace: true });
          },
        })}
      />
    </>,
  );
}
